import {
  BellRing,
  ChevronDown,
  LogIn,
  LogOut,
  MessageSquareText,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import type { RefObject } from "react";
import type { PlayerProfile } from "../types";
import { formatSectorLabel } from "../lib/sector-label";
import { playerFacingDisplayName } from "../lib/player-facing-display-name";

interface ShellHeaderProps {
  authenticated: boolean;
  authReady: boolean;
  authAvailable: boolean;
  profile?: PlayerProfile;
  guildTriggerRef: RefObject<HTMLButtonElement>;
  chatOpen: boolean;
  profileOpen: boolean;
  onLogin: () => void;
  onLogout: () => void;
  onToggleChat: () => void;
  onToggleProfile: () => void;
  onOpenGuild: () => void;
  incomingCallCount?: number;
  emailPreferenceSaving: boolean;
  profileError?: string;
  onEmailPreferenceChange: (enabled: boolean) => void;
  modalInert?: boolean;
}

export function ShellHeader({
  authenticated,
  authReady,
  authAvailable,
  profile,
  guildTriggerRef,
  chatOpen,
  profileOpen,
  onLogin,
  onLogout,
  onToggleChat,
  onToggleProfile,
  onOpenGuild,
  incomingCallCount = 0,
  emailPreferenceSaving,
  profileError,
  onEmailPreferenceChange,
  modalInert = false,
}: ShellHeaderProps) {
  const displayName = playerFacingDisplayName(profile);
  const portraitUrl = profile?.showcase?.customPortraitStatus === "approved"
    ? profile.showcase.customPortraitUrl
    : undefined;
  return (
    <header
      className="shell-header"
      aria-hidden={modalInert ? true : undefined}
      {...(modalInert ? { inert: "" } : {})}
    >
      <div className="brand-lockup" aria-label="VAELOR">
        <span className="brand-mark" aria-hidden="true">V</span>
        <span>
          <strong>VAELOR</strong>
          <small>Repousse le Miasme</small>
        </span>
      </div>

      <div className="header-status" aria-live="polite">
        <span className={`status-dot ${authenticated ? "is-online" : ""}`} />
        {authenticated
          ? "Lié au Bastion"
          : !authReady
            ? "Ouverture du sceau…"
            : authAvailable
              ? "Hors connexion"
              : "Identité à relier"}
      </div>

      <nav className="header-actions" aria-label="Actions du compte">
        {authenticated ? (
          <>
            <button
              className={`icon-command ${chatOpen ? "is-active" : ""}`}
              type="button"
              onClick={onToggleChat}
              title={chatOpen ? "Fermer le courrier" : "Ouvrir le courrier"}
              aria-label={chatOpen ? "Fermer le courrier" : "Ouvrir le courrier"}
              aria-pressed={chatOpen}
            >
              <MessageSquareText size={19} />
            </button>
            {profile && (
              <button
                ref={guildTriggerRef}
                className="guild-header-command"
                type="button"
                onClick={onOpenGuild}
                aria-haspopup="dialog"
                aria-label={`${profile.guildId ? "Voir ma Compagnie" : "Fonder une Compagnie"}${incomingCallCount > 0 ? ` · ${incomingCallCount} appel${incomingCallCount === 1 ? "" : "s"} de ralliement` : ""}`}
              >
                {profile.guildId ? "Voir ma Compagnie" : "Fonder une Compagnie"}
                {incomingCallCount > 0 && (
                  <span className="guild-header-command__count" aria-hidden="true">{incomingCallCount}</span>
                )}
              </button>
            )}
            <div
              className="profile-anchor"
              onKeyDown={(event) => {
                if (!profileOpen) return;
                const profileCommand = event.currentTarget.querySelector<HTMLButtonElement>(".profile-command");
                if (event.key === "Escape") {
                  event.preventDefault();
                  event.stopPropagation();
                  onToggleProfile();
                  profileCommand?.focus();
                  return;
                }
                if (event.key !== "Tab") return;
                const profileControls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>([
                  '.profile-popover button:not([disabled]):not([tabindex="-1"])',
                  '.profile-popover input:not([disabled]):not([tabindex="-1"])',
                  '.profile-popover select:not([disabled]):not([tabindex="-1"])',
                  '.profile-popover textarea:not([disabled]):not([tabindex="-1"])',
                  '.profile-popover a[href]:not([tabindex="-1"])',
                  '.profile-popover [tabindex]:not([tabindex="-1"])',
                ].join(", ")));
                if (profileControls.length === 0) return;
                const currentControlIndex = profileControls.indexOf(event.target as HTMLElement);
                if (!event.shiftKey && event.target === profileCommand) {
                  event.preventDefault();
                  profileControls[0]?.focus();
                  return;
                }
                if (!event.shiftKey && currentControlIndex >= 0 && currentControlIndex < profileControls.length - 1) {
                  event.preventDefault();
                  profileControls[currentControlIndex + 1]?.focus();
                  return;
                }
                if (event.shiftKey && currentControlIndex === 0 && profileCommand) {
                  event.preventDefault();
                  profileCommand.focus();
                  return;
                }
                if (event.shiftKey && currentControlIndex > 0) {
                  event.preventDefault();
                  profileControls[currentControlIndex - 1]?.focus();
                }
              }}
            >
              <button
                type="button"
                className="profile-command"
                onClick={onToggleProfile}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  onToggleProfile();
                }}
                aria-expanded={profileOpen}
                aria-haspopup="dialog"
                aria-label={`Profil joueur de ${displayName}`}
              >
                <span className="profile-avatar" aria-hidden="true">
                  {portraitUrl ? <img src={portraitUrl} alt="" referrerPolicy="no-referrer" /> : <UserRound />}
                </span>
                <span className="profile-copy">
                  <strong>{displayName}</strong>
                  <small>{profile ? `Anneau A${profile.bestRing}` : "Profil en lecture"}</small>
                </span>
                <ChevronDown size={16} />
              </button>
              {profileOpen && (
                <div className="profile-popover" role="dialog" aria-label="Profil joueur">
                  <div className="profile-popover__title">
                    <ShieldCheck size={18} />
                    <span>
                      <strong>{displayName}</strong>
                       <small>{profile ? "Monde lié au Bastion" : "Monde en cours de liaison"}</small>
                    </span>
                  </div>
                  {profile && (
                    <>
                      <dl className="profile-facts">
                         <div><dt>Secteur</dt><dd>{formatSectorLabel(profile.currentSectorId)}</dd></div>
                         <div><dt>Descente</dt><dd>Anneau A{profile.bestRing}</dd></div>
                         <div><dt>Compagnie</dt><dd>{profile.guildName || "Aucune"}</dd></div>
                         {incomingCallCount > 0 && (
                           <div><dt>Appels de ralliement</dt><dd>{incomingCallCount}</dd></div>
                         )}
                       </dl>
                      <label className="profile-email-choice">
                        <input
                          type="checkbox"
                          checked={profile.emailPreferences.reengagementEnabled}
                          disabled={!profile.emailPreferences.contactAvailable || emailPreferenceSaving}
                          onChange={(event) => onEmailPreferenceChange(event.target.checked)}
                        />
                        <BellRing size={17} aria-hidden="true" />
                        <span>
                          <strong>Relances d’inactivité</strong>
                          <small>{profile.emailPreferences.reengagementEnabled ? "Activées, révocables à tout moment" : "Désactivées"}</small>
                        </span>
                      </label>
                      {profileError && <div className="notice notice--error profile-email-error" role="alert">{profileError}</div>}
                    </>
                  )}
                  <button className="text-command danger-command" type="button" onClick={onLogout}>
                    <LogOut size={17} /> Déconnexion
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <button className="primary-command" type="button" onClick={onLogin} disabled={!authReady || !authAvailable}>
            <LogIn size={18} /> Entrer dans VAELOR
          </button>
        )}
      </nav>
    </header>
  );
}
